/**
 * MemberDirectory — CHI Alliance
 * Obsidian Pulse design system.
 * Auth-gated: only accessible when logged in.
 *
 * Two tiers, and they are different pages rather than one page with fields
 * blanked out:
 *
 * - Paid members browse. They get the industry rail, search, the category /
 *   view-all switch, and a profile for every listed member.
 * - Free members see who is here and nothing more: a photo and a name per tile,
 *   with the route to the rest being a membership rather than a click. This is
 *   not only a presentation choice — the API sends a free viewer nothing else
 *   (see buildDirectoryResponse in server/directoryPrivacy.ts), so there is no
 *   hidden detail sitting in the page waiting to be inspected.
 */
import { useState, useMemo, useEffect, useRef, type ReactElement } from "react";
import { Link } from "wouter";
import {
  Search, Crown, Users, Lock, LayoutGrid, ArrowRight,
  MapPin, SlidersHorizontal, X, Sparkles, ChevronDown,
  Briefcase, Mail, Phone, Globe, Linkedin, ChevronRight, MessageCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { formatPhoneForDisplay } from "@shared/profileInputs";
import { personAvatarColor, personInitials } from "@/lib/personDisplay";
import { rankPopulatedCategories, visibleCategoryFilters } from "@/lib/memberDirectoryFilters";
import { INDUSTRIES, OTHER_INDUSTRY, industryIcon } from "@shared/industries";
import { socialPlatform, type SocialLink } from "@shared/socialLinks";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { MemberProfilePhoto } from "@/components/PersonPortraitAvatar";

/**
 * A directory record. Every signed-in member can read the directory; how much
 * each record carries depends on whether that person holds a paid membership.
 * A free record is a name and a photo, so the business fields are empty rather
 * than absent, and `isPaidMember` drives the Member badge.
 */
interface Member {
  id: string;
  name: string;
  isPaidMember: boolean;
  title: string;
  company: string;
  industry: string;
  location: string;
  bio: string;
  interests: string[];
  linkedinUrl: string;
  socialLinks: SocialLink[];
  companyWebsiteUrl: string;
  email: string;
  phone: string;
  canMessage: boolean;
  /** Initials, shown when the member has no photo or has not published one. */
  avatar: string;
  avatarColor: string;
  photoUrl: string | null;
  memberPhotoUrl: string | null;
}

const color = personAvatarColor;

/**
 * Whether the tier preview control is available.
 *
 * `import.meta.env.DEV` is statically false in a production build, so both this
 * flag and everything it guards are dropped at build time — the control cannot
 * reach a deployed site, and there is no runtime flag for it to be turned on by.
 */
const DEV_TIER_PREVIEW = import.meta.env.DEV;

/**
 * First and last name only, mirroring nameOnly() in server/directoryPrivacy.ts.
 *
 * Used only by the dev preview: a real free viewer is sent the shortened name by
 * the API, so previewing the free tier against a paid payload has to shorten it
 * here or the preview would show more than the tier ever does.
 */
function previewShortName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] ?? "CHI Alliance Member";
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

/**
 * Which of the site's categories a member is browsed under.
 *
 * The rail lists the signup taxonomy and nothing else, so anything outside it —
 * a blank industry, or one a member typed into the "Other" field — is browsed
 * under "Other" rather than minting a new category. That keeps the list of
 * categories the same list the rest of the site uses.
 */
function categoryOf(member: Member): string {
  return INDUSTRIES.includes(member.industry) ? member.industry : OTHER_INDUSTRY;
}

/**
 * Whether this member left a way to reach them. Drives the dot on the tile —
 * it reports a published contact method, not presence, because the directory
 * has no notion of who is online and a green dot that implied otherwise would
 * be inventing a status the product does not have.
 */
function isOpenToContact(member: Member): boolean {
  return Boolean(member.email || member.linkedinUrl);
}

// ── AVATAR ────────────────────────────────────────────────────────────────────
function Avatar({ member, size }: { member: Member; size: number }) {
  return (
    <MemberProfilePhoto
      name={member.name}
      speakerPortraitUrl={member.photoUrl}
      memberPhotoUrl={member.memberPhotoUrl}
      size={size}
      accent={member.avatarColor}
    />
  );
}

// ── MEMBER TILE (paid viewer) ─────────────────────────────────────────────────
function MemberTile({ member, onOpen }: { member: Member; onOpen: (member: Member) => void }) {
  return (
    <button
      type="button"
      className="dir-tile dir-tile--profile"
      onClick={() => onOpen(member)}
      aria-label={`Open profile for ${member.name}`}
      aria-haspopup="dialog"
    >
      <span className="dir-tile__mark" aria-hidden="true">
        <Sparkles size={11} />
      </span>

      <Avatar member={member} size={86} />

      <h3 className="dir-tile__name">{member.name}</h3>

      {member.isPaidMember && (
        <span className="dir-tile__badge">
          <Crown size={9} aria-hidden="true" /> Member
        </span>
      )}

      <div className="dir-tile__meta">
        {member.title && <p className="dir-tile__title">{member.title}</p>}
        {member.company && <p className="dir-tile__company">{member.company}</p>}
        {member.email && (
          <span className="dir-tile__email">{member.email}</span>
        )}
      </div>
    </button>
  );
}

// ── MEMBER TILE (free viewer) ─────────────────────────────────────────────────
/**
 * A name and a face. The tile is the upgrade prompt rather than carrying one:
 * a row of buttons repeating "Upgrade" under every member reads as an ad, and
 * the banner above the grid already makes the offer in full.
 */
function LockedTile({ member }: { member: Member }) {
  return (
    <Link
      href="/membership"
      className="dir-tile dir-tile--locked"
      aria-label={`Join free to view ${member.name}'s profile`}
    >
      <Avatar member={member} size={86} />
      <h3 className="dir-tile__name">{member.name}</h3>
      <span className="dir-tile__locked-hint">
        <Lock size={11} aria-hidden="true" />
        <span className="dir-tile__locked-default">Profile locked</span>
        <span className="dir-tile__locked-hover">Upgrade to view</span>
      </span>
    </Link>
  );
}

// ── PROFILE DIALOG ────────────────────────────────────────────────────────────
/**
 * The mark that closes the dialog. Decorative, so it is hidden from assistive
 * technology rather than described — it carries no information the profile above
 * it has not already given.
 */
function LotusMark() {
  return (
    <svg width="34" height="18" viewBox="0 0 34 18" fill="none" aria-hidden="true" focusable="false">
      {/* Five petals fanned from one point at the base: the middle petal drawn
          upright, the outer four the same shape rotated about that point. */}
      <g stroke="#E91E8C" strokeWidth="1.1" strokeLinejoin="round" opacity="0.9" transform="translate(17 16.5)">
        <path d="M0 0C-3 -4 -3 -9.5 0 -13.5C3 -9.5 3 -4 0 0Z" />
        <path d="M0 0C-3 -4 -3 -9.5 0 -13.5C3 -9.5 3 -4 0 0Z" transform="rotate(-38)" />
        <path d="M0 0C-3 -4 -3 -9.5 0 -13.5C3 -9.5 3 -4 0 0Z" transform="rotate(38)" />
        <path d="M0 0C-2.4 -3 -2.4 -7.2 0 -10.2C2.4 -7.2 2.4 -3 0 0Z" transform="rotate(-72)" />
        <path d="M0 0C-2.4 -3 -2.4 -7.2 0 -10.2C2.4 -7.2 2.4 -3 0 0Z" transform="rotate(72)" />
      </g>
    </svg>
  );
}

/**
 * One reachable destination on a profile: an icon, the value itself, and what
 * kind of value it is. `external` marks the rows that leave the site, which is
 * both the chevron and the reason for target/rel.
 */
interface ContactRow {
  key: string;
  icon: ReactElement;
  value: string;
  label: string;
  href: string;
  external: boolean;
  /** Renders the icon tile in the destination's own colour rather than CHI pink. */
  tone?: "linkedin";
}

function contactRows(member: Member): ContactRow[] {
  const rows: ContactRow[] = [];
  if (member.email) {
    rows.push({
      key: "email", icon: <Mail size={16} />, value: member.email,
      label: "Email", href: `mailto:${member.email}`, external: false,
    });
  }
  if (member.phone) {
    rows.push({
      /* Numbers the formatter cannot parse are shown as the member typed them
         rather than dropped from the profile. */
      key: "phone", icon: <Phone size={16} />, value: formatPhoneForDisplay(member.phone) ?? member.phone,
      label: "Phone", href: `tel:${member.phone}`, external: false,
    });
  }
  if (member.linkedinUrl) {
    rows.push({
      key: "linkedin", icon: <Linkedin size={16} />, value: "LinkedIn",
      label: "Connect", href: member.linkedinUrl, external: true, tone: "linkedin",
    });
  }
  if (member.companyWebsiteUrl) {
    rows.push({
      key: "website", icon: <Globe size={16} />,
      value: member.companyWebsiteUrl.replace(/^https?:\/\//, "").replace(/\/$/, ""),
      label: "Website", href: member.companyWebsiteUrl, external: true,
    });
  }
  for (const link of member.socialLinks) {
    rows.push({
      key: link.url, icon: <Globe size={16} />,
      value: socialPlatform(link.platform)?.label ?? "Link",
      label: "Profile", href: link.url, external: true,
    });
  }
  return rows;
}

function ProfileDialog({ member, onClose }: { member: Member; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);

  /*
   * Whether the biography has more below the fold.
   *
   * Measured rather than assumed: most biographies fit, and a permanent "scroll
   * for more" on those is a lie. Recomputed on scroll and on resize, because the
   * modal's width decides how many lines the text takes.
   */
  const bioRef = useRef<HTMLDivElement>(null);
  const [bioHasMore, setBioHasMore] = useState(false);

  function updateBioScroll() {
    const node = bioRef.current;
    if (!node) return;
    // A pixel of tolerance: fractional scroll heights otherwise never reach zero.
    setBioHasMore(node.scrollHeight - node.scrollTop - node.clientHeight > 1);
  }

  useEffect(() => {
    updateBioScroll();
    const node = bioRef.current;
    if (!node || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateBioScroll);
      return () => window.removeEventListener("resize", updateBioScroll);
    }
    const observer = new ResizeObserver(updateBioScroll);
    observer.observe(node);
    return () => observer.disconnect();
  }, [member.id]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // The page behind a modal should not scroll away under it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  /* A bio written in paragraphs is read as paragraphs rather than one block. */
  const paragraphs = member.bio.split(/\n{2,}/).map(part => part.trim()).filter(Boolean);
  const contacts = contactRows(member);
  const subtitle = INDUSTRIES.includes(member.industry) ? member.industry : "";

  return (
    <div className="dir-modal" onClick={onClose}>
      <div
        className="dir-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dir-modal-name"
        onClick={(event) => event.stopPropagation()}
      >
        <button ref={closeRef} type="button" className="dir-modal__close" onClick={onClose} aria-label="Close profile">
          <X size={18} />
        </button>

        <div className="dir-modal__head">
          {/* Portrait and identity share the first row; the biography takes the
              full width beneath them, where a long one has room to be read. */}
          <div className="dir-modal__portrait">
            <Avatar member={member} size={148} />
            {/* Published contact details, not presence — see isOpenToContact. */}
            {isOpenToContact(member) && (
              <span className="dir-modal__reachable" title="Open to contact" aria-hidden="true" />
            )}
          </div>

          <div className="dir-modal__identity">
            <h2 id="dir-modal-name" className="dir-modal__name">{member.name}</h2>
            {subtitle && <p className="dir-modal__industry">{subtitle}</p>}
            {member.company && <p className="dir-modal__company">{member.company}</p>}

            {(member.title || member.location) && (
              <div className="dir-modal__facts">
                {member.title && (
                  <p className="dir-modal__fact">
                    <Briefcase size={15} aria-hidden="true" /> {member.title}
                  </p>
                )}
                {member.location && (
                  <p className="dir-modal__fact">
                    <MapPin size={15} aria-hidden="true" /> {member.location}
                  </p>
                )}
              </div>
            )}
          </div>

          {paragraphs.length > 0 && (
            /*
              The scroll state drives the affordance rather than a permanent fade.
              A gradient that is always there reads as decoration and says nothing
              about whether anything is below it; this one appears only while there
              is more to read, and goes when you reach the end.
            */
            <div className="dir-modal__bioWrap" data-more={bioHasMore || undefined}>
              <div
                ref={bioRef}
                className="dir-modal__bio"
                tabIndex={0}
                aria-label={`${member.name}'s biography`}
                onScroll={updateBioScroll}
              >
                {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
              </div>
              {bioHasMore && (
                <span className="dir-modal__bioMore" aria-hidden="true">
                  <ChevronDown size={13} /> Scroll for more
                </span>
              )}
            </div>
          )}
        </div>

        {member.interests.length > 0 && (
          <div className="dir-modal__card">
            <p className="dir-modal__label">Interests</p>
            <div className="dir-modal__chips">
              {member.interests.map((interest) => (
                <span key={interest} className="dir-modal__chip">
                  <Sparkles size={13} aria-hidden="true" /> {interest}
                </span>
              ))}
            </div>
          </div>
        )}

        {member.canMessage && (
          <div className="dir-modal__card">
            <Link
              href={`/messages?member=${encodeURIComponent(member.id)}`}
              className="dir-modal__message-action"
              onClick={onClose}
            >
              <MessageCircle size={16} aria-hidden="true" />
              Start a private conversation
              <ChevronRight size={16} aria-hidden="true" />
            </Link>
            <p style={{ margin: ".55rem 0 0", color: "#8491A7", fontFamily: "'Inter', sans-serif", fontSize: ".72rem" }}>
              This member has chosen to receive messages from the community.
            </p>
          </div>
        )}

        {contacts.length > 0 && (
          <div className="dir-modal__card">
            <p className="dir-modal__label">Contact</p>
            {/* rel="noreferrer" on every outbound row: these are member-supplied
                destinations, so the referring page is not theirs to receive. */}
            <div className="dir-modal__contacts">
              {contacts.map((row) => (
                <a
                  key={row.key}
                  className="dir-modal__contact"
                  href={row.href}
                  {...(row.external ? { target: "_blank", rel: "noreferrer" } : {})}
                >
                  <span
                    className={`dir-modal__contact-icon${row.tone ? ` dir-modal__contact-icon--${row.tone}` : ""}`}
                    aria-hidden="true"
                  >
                    {row.icon}
                  </span>
                  <span className="dir-modal__contact-text">
                    <span className="dir-modal__contact-value">{row.value}</span>
                    <span className="dir-modal__contact-label">{row.label}</span>
                  </span>
                  {row.external && (
                    <ChevronRight className="dir-modal__contact-go" size={16} aria-hidden="true" />
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="dir-modal__mark">
          <span className="dir-modal__rule" aria-hidden="true" />
          <LotusMark />
          <span className="dir-modal__rule" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function MemberDirectory() {
  const { isLoggedIn } = useAuth();
  const directoryQuery = trpc.members.directory.useQuery(undefined, { enabled: isLoggedIn, retry: false });

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [mode, setMode] = useState<"all" | "categories">("all");
  const [showRefine, setShowRefine] = useState(false);
  const [contactableOnly, setContactableOnly] = useState(false);
  const [sortDescending, setSortDescending] = useState(false);
  const [openProfile, setOpenProfile] = useState<Member | null>(null);
  const [showAllCategories, setShowAllCategories] = useState(false);
  /** Local-dev only: render the page as the other tier sees it. */
  const [previewFreeTier, setPreviewFreeTier] = useState(false);

  const actualAccess = directoryQuery.data?.viewerAccess ?? "basic";
  const viewerAccess = DEV_TIER_PREVIEW && previewFreeTier ? "basic" : actualAccess;
  /** True only while a paid session is previewing the free tier in local dev. */
  const previewingFree = DEV_TIER_PREVIEW && previewFreeTier && actualAccess === "paid";
  const directoryMembers = useMemo<Member[]>(() => (directoryQuery.data?.members ?? []).map((member, index) => {
    const fullName = member.name?.trim() || "CHI Alliance Member";
    const name = previewingFree ? previewShortName(fullName) : fullName;
    const initials = personInitials(name);
    return {
      id: member.id,
      name,
      isPaidMember: member.isPaidMember ?? false,
      title: member.title ?? "",
      company: member.company ?? "",
      industry: member.industry ?? "",
      location: member.location ?? "",
      bio: member.bio ?? "",
      interests: member.interests ?? [],
      linkedinUrl: member.linkedinUrl ?? "",
      socialLinks: member.socialLinks ?? [],
      companyWebsiteUrl: member.companyWebsiteUrl ?? "",
      email: member.email ?? "",
      phone: member.phone ?? "",
      canMessage: member.canMessage ?? false,
      avatar: initials,
      avatarColor: color(index),
      photoUrl: member.photoUrl ?? null,
      memberPhotoUrl: member.memberPhotoUrl ?? null,
    };
  }), [directoryQuery.data, previewingFree]);

  /**
   * Only categories with people in them earn a place in the browse rail. The
   * most populated groups lead so the short default view is useful immediately.
   */
  const categories = useMemo(() => {
    return rankPopulatedCategories(directoryMembers.map(categoryOf));
  }, [directoryMembers]);

  const visibleCategories = useMemo(
    () => visibleCategoryFilters(categories, showAllCategories),
    [categories, showAllCategories],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const rows = directoryMembers.filter((m) => {
      const matchesSearch = !q || [m.name, m.company, m.title, m.industry, m.bio].some((f) => f.toLowerCase().includes(q));
      const matchesCategory = !category || categoryOf(m) === category;
      const matchesContactable = !contactableOnly || isOpenToContact(m);
      return matchesSearch && matchesCategory && matchesContactable;
    });
    rows.sort((a, b) => (sortDescending ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name)));
    return rows;
  }, [directoryMembers, search, category, contactableOnly, sortDescending]);

  /** Filtered members bucketed by industry, for the Categories view. */
  const grouped = useMemo(() => {
    const map = new Map<string, Member[]>();
    for (const member of filtered) {
      const key = categoryOf(member);
      const bucket = map.get(key);
      if (bucket) bucket.push(member);
      else map.set(key, [member]);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const refineCount = (contactableOnly ? 1 : 0) + (sortDescending ? 1 : 0);

  // ── AUTH GATE ──────────────────────────────────────────────────────────────
  if (!isLoggedIn) {
    return (
      <>
        <Navigation />
        <div style={{ minHeight: "100vh", background: "#080C18", display: "flex", alignItems: "center", justifyContent: "center", padding: "6rem 1.5rem 4rem" }}>
          <div style={{ textAlign: "center", maxWidth: 460 }}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%", margin: "0 auto 1.5rem",
              background: "rgba(233,30,140,0.08)", border: "1px solid rgba(233,30,140,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Lock size={28} style={{ color: "#E91E8C" }} />
            </div>
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.75rem, 5vw, 2.5rem)", color: "white", marginBottom: "0.75rem", letterSpacing: "0.02em" }}>
              Members Only
            </h1>
            <p style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.75, marginBottom: "2rem" }}>
              The CHI Alliance Member Directory is exclusively available to verified members. Sign in or create a free account to connect with the community.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <Link href="/login">
                <button style={{
                  width: "100%", padding: "0.9rem 2rem",
                  background: "linear-gradient(135deg, #E91E8C, #C2185B)",
                  border: "none", borderRadius: "0.625rem", color: "white",
                  fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
                  fontSize: "0.9rem", letterSpacing: "0.04em", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
                  boxShadow: "0 4px 20px rgba(233,30,140,0.35)",
                }}>
                  Sign In to Your Account <ArrowRight size={16} />
                </button>
              </Link>
              <Link href="/create-account">
                <button style={{
                  width: "100%", padding: "0.9rem 2rem",
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.12)", borderRadius: "0.625rem", color: "#94A3B8",
                  fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
                  fontSize: "0.9rem", cursor: "pointer",
                }}>
                  Create a Free Account
                </button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (directoryQuery.isLoading) {
    return <><Navigation /><div style={{ minHeight: "100vh", background: "#080C18", display: "grid", placeItems: "center", padding: "6rem 1.5rem 4rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif" }}>Loading your member access…</div><Footer /></>;
  }

  if (directoryQuery.isError) {
    return (
      <>
        <Navigation />
        <div style={{ minHeight: "100vh", background: "#080C18", display: "flex", alignItems: "center", justifyContent: "center", padding: "6rem 1.5rem 4rem" }}>
          <div style={{ textAlign: "center", maxWidth: 460 }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", margin: "0 auto 1.5rem", background: "rgba(233,30,140,0.08)", border: "1px solid rgba(233,30,140,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}><Lock size={28} style={{ color: "#E91E8C" }} /></div>
            <h1 style={{ color: "white", fontFamily: "'Bebas Neue', sans-serif", fontSize: "2rem", margin: "0 0 .75rem" }}>Directory unavailable</h1>
            <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", lineHeight: 1.65, margin: "0 0 1.5rem" }}>The member directory could not be loaded. Try again in a moment, or sign in again if the problem continues.</p>
            <button type="button" onClick={() => directoryQuery.refetch()} style={{ padding: ".8rem 1.2rem", border: 0, borderRadius: ".65rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: "pointer" }}>Try again</button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const isPaid = viewerAccess === "paid";
  const headingLabel = category || "All Members";

  // ── AUTHENTICATED VIEW ─────────────────────────────────────────────────────
  return (
    <>
      <Navigation />
      <div className="dir-page">
        <div className="dir-glow" aria-hidden="true" />

        <div className="dir-shell">
          {/*
            Local-dev tier preview. Stripped from production builds along with
            DEV_TIER_PREVIEW, and it only ever narrows the view: previewing the
            free tier hides fields the paid payload already contains, while the
            reverse is impossible because the API sends a free viewer nothing to
            reveal. So this cannot be used to see more than the account may see.
          */}
          {DEV_TIER_PREVIEW && (
            <>
            {/* Styles live inside the guard too, so a production build ships
                neither the control nor the rules that would have dressed it. */}
            <style>{`
  /* ── Local-dev tier preview ── */
  .dir-devtier {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
    justify-content: flex-end;
    margin-bottom: 1.1rem;
  }

  .dir-devtier__tag {
    margin-right: 0.15rem;
    padding: 0.2rem 0.5rem;
    border: 1px solid rgba(245,166,35,0.4);
    border-radius: 0.4rem;
    background: rgba(245,166,35,0.12);
    color: #F5A623;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }

  .dir-devtier button {
    padding: 0.4rem 0.7rem;
    border: 1px dashed rgba(245,166,35,0.35);
    border-radius: 0.5rem;
    background: none;
    color: #A9B6C9;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
  }

  .dir-devtier button[data-active] {
    border-style: solid;
    border-color: rgba(245,166,35,0.6);
    background: rgba(245,166,35,0.16);
    color: #FBD38D;
  }

  .dir-devtier__note {
    flex-basis: 100%;
    color: #8A99B0;
    font-family: 'Inter', sans-serif;
    font-size: 0.72rem;
    text-align: right;
  }
            `}</style>
            <div className="dir-devtier" role="group" aria-label="Local dev: preview membership tier">
              <span className="dir-devtier__tag">Local dev</span>
              <button
                type="button"
                data-active={!previewFreeTier || undefined}
                aria-pressed={!previewFreeTier}
                onClick={() => setPreviewFreeTier(false)}
              >
                Paid member
              </button>
              <button
                type="button"
                data-active={previewFreeTier || undefined}
                aria-pressed={previewFreeTier}
                onClick={() => setPreviewFreeTier(true)}
              >
                Free account
              </button>
              {actualAccess === "basic" && (
                <span className="dir-devtier__note" role="note">
                  Signed in as a free account — the paid view needs a paid session, because
                  the API sends this account no paid fields to show.
                </span>
              )}
            </div>
            </>
          )}

          {/* ── Masthead ── */}
          <header className="dir-masthead">
            <div className="dir-masthead__lede">
              <p className="dir-eyebrow">
                <span className="dir-eyebrow__mark" aria-hidden="true" />
                Member Directory
              </p>
              <h1 className="dir-title">Connect With the Community</h1>
              <p className="dir-sub">
                {isPaid
                  ? "Discover and connect with members across industries and expertise."
                  : "See who is part of The CHI Alliance. Profiles open up with a free membership."}
              </p>
            </div>

            {isPaid && (
              <div className="dir-masthead__tools">
                <div className="dir-search">
                  <Search size={15} aria-hidden="true" />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search by name, company, or title..."
                    aria-label="Search members by name, company, or title"
                  />
                </div>
                <div className="dir-switch" role="group" aria-label="Directory layout">
                  <button
                    type="button"
                    data-active={mode === "categories" || undefined}
                    aria-pressed={mode === "categories"}
                    onClick={() => setMode("categories")}
                  >
                    <LayoutGrid size={13} aria-hidden="true" /> Categories
                  </button>
                  <button
                    type="button"
                    data-active={mode === "all" || undefined}
                    aria-pressed={mode === "all"}
                    onClick={() => { setMode("all"); setCategory(""); }}
                  >
                    <Users size={13} aria-hidden="true" /> View All
                  </button>
                </div>

                {/*
                  Filter sits with search and the layout switch rather than above the
                  results. All three narrow the same list, and having one of them
                  further down the page made it read as belonging to the results
                  instead of to the controls that produce them.
                */}
                  <button
                    type="button"
                    className="dir-refine"
                    data-active={refineCount > 0 || undefined}
                    aria-expanded={showRefine}
                    aria-controls="dir-refine-panel"
                    onClick={() => setShowRefine(!showRefine)}
                  >
                    <SlidersHorizontal size={13} aria-hidden="true" />
                    Filter
                    {refineCount > 0 && <span className="dir-refine__count">{refineCount}</span>}
                    <ChevronDown size={13} aria-hidden="true" style={{ transform: showRefine ? "rotate(180deg)" : undefined, transition: "transform .18s ease" }} />
                  </button>
              </div>
            )}
          </header>

          {/* ── Free tier: the offer, made once, above the grid ── */}
          {!isPaid && (
            <div className="dir-upsell">
              <div>
                <strong>See more than a name</strong>
                <span>
                  A free membership opens every profile: role, company, industry, interests,
                  and how to reach them.
                </span>
              </div>
              <Link href="/membership" className="dir-upsell__cta">
                Join free <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </div>
          )}

          <div className={isPaid ? "dir-body" : "dir-body dir-body--solo"}>
            {/* ── Industry rail ── */}
            {isPaid && (
              <aside className="dir-rail" aria-label="Browse by industry">
                <div className="dir-rail__head">
                  <p className="dir-rail__eyebrow">Browse by industry</p>
                  <h2 className="dir-rail__title">Categories</h2>
                </div>
                <div className="dir-rail__scroll">
                  <button
                    type="button"
                    className="dir-cat"
                    data-active={category === "" || undefined}
                    aria-pressed={category === ""}
                    onClick={() => setCategory("")}
                  >
                    <span className="dir-cat__icon dir-cat__icon--all" aria-hidden="true">
                      <Sparkles size={12} />
                    </span>
                    <span className="dir-cat__label">All Members</span>
                    <span className="dir-cat__count">{directoryMembers.length}</span>
                  </button>
                  {visibleCategories.map(({ name, count }) => (
                    <button
                      key={name}
                      type="button"
                      className="dir-cat"
                      data-active={category === name || undefined}
                      aria-pressed={category === name}
                      onClick={() => setCategory(category === name ? "" : name)}
                    >
                      <span className="dir-cat__icon" aria-hidden="true">{industryIcon(name)}</span>
                      <span className="dir-cat__label">{name}</span>
                      <span className="dir-cat__count">{count}</span>
                    </button>
                  ))}
                  {categories.length > 5 && (
                    <button
                      type="button"
                      className="dir-cat dir-cat--expand"
                      aria-expanded={showAllCategories}
                      onClick={() => setShowAllCategories(value => !value)}
                    >
                      <span className="dir-cat__icon" aria-hidden="true"><ChevronDown size={14} /></span>
                      <span className="dir-cat__label">{showAllCategories ? "Show fewer filters" : "View all filters"}</span>
                      <span className="dir-cat__count">{categories.length}</span>
                    </button>
                  )}
                </div>
              </aside>
            )}

            {/* ── Results ── */}
            <main className="dir-main">
              <div className="dir-main__head">
                <div>
                  <h2 className="dir-main__title">{isPaid ? headingLabel : "All Members"}</h2>
                  <p className="dir-main__count">
                    {filtered.length} member{filtered.length === 1 ? "" : "s"} found
                  </p>
                </div>
              </div>

              {isPaid && showRefine && (
                <div className="dir-refine__panel" id="dir-refine-panel">
                  <label className="dir-refine__row">
                    <input
                      type="checkbox"
                      checked={contactableOnly}
                      onChange={(event) => setContactableOnly(event.target.checked)}
                    />
                    Only members open to contact
                  </label>
                  <label className="dir-refine__row">
                    <input
                      type="checkbox"
                      checked={sortDescending}
                      onChange={(event) => setSortDescending(event.target.checked)}
                    />
                    Sort Z–A
                  </label>
                  {refineCount > 0 && (
                    <button
                      type="button"
                      className="dir-refine__clear"
                      onClick={() => { setContactableOnly(false); setSortDescending(false); }}
                    >
                      <X size={12} aria-hidden="true" /> Clear
                    </button>
                  )}
                </div>
              )}

              {filtered.length === 0 ? (
                <div className="dir-empty">
                  <Users size={30} aria-hidden="true" />
                  <p>No members match what you are looking for.</p>
                  {isPaid && (search || category || refineCount > 0) && (
                    <button
                      type="button"
                      onClick={() => { setSearch(""); setCategory(""); setContactableOnly(false); setSortDescending(false); }}
                    >
                      Clear search and filters
                    </button>
                  )}
                </div>
              ) : !isPaid ? (
                <div className="dir-grid">
                  {filtered.map((member) => <LockedTile key={member.id} member={member} />)}
                </div>
              ) : mode === "all" ? (
                <div className="dir-grid">
                  {filtered.map((member) => (
                    <MemberTile key={member.id} member={member} onOpen={setOpenProfile} />
                  ))}
                </div>
              ) : (
                grouped.map(([industry, members]) => (
                  <section key={industry} className="dir-group">
                    <h3 className="dir-group__head">
                      <span aria-hidden="true">{industryIcon(industry)}</span>
                      {industry}
                      <span className="dir-group__count">{members.length}</span>
                    </h3>
                    <div className="dir-grid">
                      {members.map((member) => (
                        <MemberTile key={member.id} member={member} onOpen={setOpenProfile} />
                      ))}
                    </div>
                  </section>
                ))
              )}
            </main>
          </div>
        </div>
      </div>

      {openProfile && <ProfileDialog member={openProfile} onClose={() => setOpenProfile(null)} />}

      <style>{`
        .dir-page {
          position: relative;
          min-height: 100vh;
          padding-top: 5rem;
          background: #080C18;
        }

        .dir-glow {
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background:
            radial-gradient(ellipse at 12% -6%, rgba(233,30,140,0.16) 0%, transparent 52%),
            radial-gradient(ellipse at 88% 104%, rgba(0,212,255,0.06) 0%, transparent 55%);
        }

        .dir-shell {
          position: relative;
          z-index: 1;
          max-width: 1360px;
          margin: 0 auto;
          padding: 2.25rem 1.5rem 5rem;
        }

        /* ── Masthead ── */
        .dir-masthead {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 2rem;
          flex-wrap: wrap;
          margin-bottom: 2.25rem;
        }

        .dir-eyebrow {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          margin: 0 0 0.85rem;
          color: #E91E8C;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .dir-eyebrow__mark {
          width: 0.42rem;
          height: 0.42rem;
          border-radius: 50%;
          background: #E91E8C;
          box-shadow: 0 0 12px rgba(233,30,140,0.9);
        }

        /*
          DM Serif Display in natural case, the same voice the home hero uses.
          The directory is the most utilitarian page on the site, and the serif
          is what keeps it reading as part of the same publication.
        */
        .dir-title {
          max-width: 15ch;
          margin: 0 0 0.75rem;
          color: #fff;
          font-family: 'DM Serif Display', serif;
          font-size: clamp(2.35rem, 4.6vw, 3.6rem);
          font-weight: 400;
          line-height: 1.02;
          letter-spacing: -0.015em;
        }

        .dir-sub {
          max-width: 46ch;
          margin: 0;
          color: #8A99B0;
          font-family: 'Inter', sans-serif;
          font-size: 0.88rem;
          line-height: 1.6;
        }

        .dir-masthead__tools {
          display: flex;
          align-items: center;
          gap: 0.7rem;
          flex-wrap: wrap;
        }

        .dir-search {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          min-width: min(21rem, 68vw);
          padding: 0.72rem 1rem;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 0.7rem;
          background: rgba(255,255,255,0.035);
          color: #475569;
          transition: border-color .18s ease, background .18s ease;
        }

        .dir-search:focus-within {
          border-color: rgba(233,30,140,0.45);
          background: rgba(233,30,140,0.05);
        }

        .dir-search input {
          flex: 1;
          min-width: 0;
          border: 0;
          background: none;
          outline: none;
          color: #fff;
          font-family: 'Inter', sans-serif;
          font-size: 0.83rem;
        }

        .dir-search input::placeholder { color: #55637A; }

        .dir-switch {
          display: flex;
          padding: 0.28rem;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 0.7rem;
          background: rgba(255,255,255,0.035);
        }

        .dir-switch button {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.5rem 0.85rem;
          border: 0;
          border-radius: 0.5rem;
          background: none;
          color: #8A99B0;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.78rem;
          font-weight: 600;
          cursor: pointer;
          transition: background .18s ease, color .18s ease;
        }

        .dir-switch button:hover { color: #fff; }

        .dir-switch button[data-active] {
          background: linear-gradient(135deg, #E91E8C, #B4166E);
          color: #fff;
          box-shadow: 0 3px 14px rgba(233,30,140,0.35);
        }

        /* ── Free-tier offer ── */
        .dir-upsell {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.25rem;
          flex-wrap: wrap;
          margin-bottom: 2rem;
          padding: 1.15rem 1.35rem;
          border: 1px solid rgba(233,30,140,0.28);
          border-radius: 0.95rem;
          background: linear-gradient(120deg, rgba(233,30,140,0.13), rgba(0,212,255,0.05));
        }

        .dir-upsell strong {
          display: block;
          margin-bottom: 0.2rem;
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.95rem;
        }

        .dir-upsell span {
          color: #A9B6C9;
          font-family: 'Inter', sans-serif;
          font-size: 0.83rem;
          line-height: 1.55;
        }

        .dir-upsell__cta {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.75rem 1.15rem;
          border-radius: 0.65rem;
          background: linear-gradient(135deg, #E91E8C, #C2185B);
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.82rem;
          font-weight: 700;
          text-decoration: none;
          white-space: nowrap;
          box-shadow: 0 4px 18px rgba(233,30,140,0.32);
        }

        /* ── Body ── */
        .dir-body {
          /* The height both column heads reserve, so their content starts level. */
          --dir-head-height: 3.5rem;
          display: grid;
          grid-template-columns: 17.5rem minmax(0, 1fr);
          gap: 1.5rem;
          align-items: start;
        }

        .dir-body--solo { grid-template-columns: minmax(0, 1fr); }

        /* ── Industry rail ── */
        .dir-rail {
          position: sticky;
          top: 6rem;
          padding: 1.15rem 0.9rem 1rem;
          /* Stacked below the rail's padding, the shared head height does the aligning. */
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 1rem;
          background: rgba(255,255,255,0.022);
        }

        .dir-rail__eyebrow {
          margin: 0 0 0.35rem 0.35rem;
          color: #55637A;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.62rem;
          font-weight: 700;
          letter-spacing: 0.13em;
          text-transform: uppercase;
        }

        /* Matches .dir-main__head, so the first category lines up with the first card. */
        .dir-rail__head {
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          min-height: var(--dir-head-height);
          margin-bottom: 1.15rem;
        }

        .dir-rail__title {
          margin: 0 0 0 0.35rem;
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.02rem;
          font-weight: 700;
        }

        .dir-rail__scroll {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          max-height: min(60vh, 34rem);
          padding-right: 0.35rem;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(233,30,140,0.35) transparent;
        }

        .dir-rail__scroll::-webkit-scrollbar { width: 5px; }
        .dir-rail__scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.04); border-radius: 99px; }
        .dir-rail__scroll::-webkit-scrollbar-thumb { background: rgba(233,30,140,0.4); border-radius: 99px; }

        .dir-cat {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          width: 100%;
          padding: 0.55rem 0.6rem;
          border: 1px solid transparent;
          border-radius: 0.65rem;
          background: none;
          color: #A9B6C9;
          font-family: 'Inter', sans-serif;
          font-size: 0.8rem;
          text-align: left;
          cursor: pointer;
          transition: background .16s ease, border-color .16s ease, color .16s ease;
        }

        .dir-cat:hover { background: rgba(255,255,255,0.045); color: #fff; }

        .dir-cat[data-active] {
          border-color: rgba(233,30,140,0.42);
          background: rgba(233,30,140,0.12);
          color: #fff;
          font-weight: 600;
        }

        .dir-cat--expand {
          margin-top: 0.35rem;
          border-top-color: rgba(255,255,255,0.08);
          color: #F9A8D4;
        }

        .dir-cat--expand .dir-cat__icon { color: #F9A8D4; }
        .dir-cat--expand[aria-expanded="true"] .dir-cat__icon svg { transform: rotate(180deg); }
        .dir-cat--expand .dir-cat__icon svg { transition: transform .18s ease; }

        .dir-cat__icon {
          display: grid;
          place-items: center;
          flex-shrink: 0;
          width: 1.65rem;
          height: 1.65rem;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 0.5rem;
          background: rgba(255,255,255,0.05);
          font-size: 0.82rem;
          line-height: 1;
        }

        .dir-cat__icon--all {
          border-color: rgba(233,30,140,0.4);
          background: rgba(233,30,140,0.16);
          color: #F9A8D4;
        }

        .dir-cat__label { flex: 1; min-width: 0; }

        .dir-cat__count {
          flex-shrink: 0;
          min-width: 1.4rem;
          padding: 0.1rem 0.35rem;
          border-radius: 99px;
          background: rgba(255,255,255,0.06);
          color: #8A99B0;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.68rem;
          font-weight: 700;
          text-align: center;
        }

        .dir-cat[data-active] .dir-cat__count {
          background: rgba(233,30,140,0.25);
          color: #F9A8D4;
        }

        /* ── Results header ── */
        /*
          Fixed height, shared with the rail's own heading block below.

          The results column has a heading above its cards and the rail does not, so
          the rail's first category button started level with the results *title*
          while the first member card started well below it — the two columns began at
          different places. Pinning both heading blocks to one height lands the first
          category and the first card on the same line.

          A height rather than a padding on the rail: the value then lives in one
          place, and the two blocks cannot drift apart when either one's type changes.
        */
        .dir-main__head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
          min-height: var(--dir-head-height);
          margin-bottom: 1.15rem;
        }

        .dir-main__title {
          margin: 0;
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.35rem;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .dir-main__count {
          margin: 0.25rem 0 0;
          color: #64748B;
          font-family: 'Inter', sans-serif;
          font-size: 0.8rem;
        }

        .dir-refine {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          flex-shrink: 0;
          padding: 0.6rem 0.85rem;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 0.65rem;
          background: rgba(255,255,255,0.035);
          color: #A9B6C9;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.78rem;
          font-weight: 600;
          cursor: pointer;
          transition: border-color .16s ease, color .16s ease;
        }

        .dir-refine:hover { color: #fff; border-color: rgba(255,255,255,0.2); }

        .dir-refine[data-active] {
          border-color: rgba(233,30,140,0.4);
          background: rgba(233,30,140,0.12);
          color: #F9A8D4;
        }

        .dir-refine__count {
          display: grid;
          place-items: center;
          width: 1.05rem;
          height: 1.05rem;
          border-radius: 50%;
          background: #E91E8C;
          color: #fff;
          font-size: 0.62rem;
        }

        .dir-refine__panel {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          flex-wrap: wrap;
          margin-bottom: 1.25rem;
          padding: 0.85rem 1rem;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 0.8rem;
          background: rgba(255,255,255,0.022);
        }

        .dir-refine__row {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          color: #A9B6C9;
          font-family: 'Inter', sans-serif;
          font-size: 0.8rem;
          cursor: pointer;
        }

        .dir-refine__row input { accent-color: #E91E8C; cursor: pointer; }

        .dir-refine__clear {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          margin-left: auto;
          border: 0;
          background: none;
          color: #E91E8C;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
        }

        /* ── Grid + tiles ── */
        .dir-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(11.5rem, 1fr));
          gap: 0.85rem;
        }

        .dir-tile {
          position: relative;
          display: flex;
          /* Full row height, so the View Profile buttons line up across a row
             whatever mix of title, company, and email the records carry. */
          height: 100%;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 2.1rem 1rem 1.1rem;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 1rem;
          background: rgba(255,255,255,0.028);
          text-align: center;
          transition: transform .18s ease, border-color .18s ease, background .18s ease;
        }

        .dir-tile--profile {
          width: 100%;
          appearance: none;
          color: inherit;
          cursor: pointer;
          font: inherit;
        }

        .dir-tile--profile:focus-visible {
          outline: 2px solid #F9A8D4;
          outline-offset: 3px;
          border-color: rgba(233,30,140,0.55);
        }

        .dir-tile:hover {
          transform: translateY(-2px);
          border-color: rgba(233,30,140,0.28);
          background: rgba(255,255,255,0.045);
        }

        .dir-tile__mark {
          position: absolute;
          top: 0.7rem;
          left: 0.7rem;
          display: grid;
          place-items: center;
          width: 1.4rem;
          height: 1.4rem;
          border: 1px solid rgba(233,30,140,0.35);
          border-radius: 0.45rem;
          background: rgba(233,30,140,0.16);
          color: #F9A8D4;
        }

        .dir-tile__name {
          margin: 0.35rem 0 0;
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.92rem;
          font-weight: 700;
          line-height: 1.25;
        }

        .dir-tile__badge {
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
          padding: 0.12rem 0.45rem;
          border: 1px solid rgba(233,30,140,0.35);
          border-radius: 99px;
          background: rgba(233,30,140,0.12);
          color: #F9A8D4;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.62rem;
          font-weight: 700;
          letter-spacing: 0.02em;
        }

        .dir-tile__meta {
          display: flex;
          flex: 1;
          flex-direction: column;
          gap: 0.1rem;
          width: 100%;
          margin-top: 0.15rem;
        }

        .dir-tile__title,
        .dir-tile__company {
          margin: 0;
          overflow: hidden;
          font-family: 'Inter', sans-serif;
          font-size: 0.74rem;
          line-height: 1.4;
          text-overflow: ellipsis;
        }

        .dir-tile__title { color: #94A3B8; }
        .dir-tile__company { color: #64748B; }

        .dir-tile__email {
          display: block;
          margin-top: 0.2rem;
          overflow: hidden;
          color: #67E8F9;
          font-family: 'Inter', sans-serif;
          font-size: 0.72rem;
          text-decoration: none;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* ── Free-tier tile ── */
        .dir-tile--locked {
          padding: 1.6rem 1rem 1.35rem;
          text-decoration: none;
          cursor: pointer;
        }

        .dir-tile__locked-hint {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          color: #55637A;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.7rem;
          font-weight: 600;
        }

        .dir-tile__locked-hover { display: none; }

        .dir-tile--locked:hover .dir-tile__locked-hint,
        .dir-tile--locked:focus-visible .dir-tile__locked-hint { color: #F9A8D4; }

        .dir-tile--locked:hover .dir-tile__locked-default,
        .dir-tile--locked:focus-visible .dir-tile__locked-default { display: none; }

        .dir-tile--locked:hover .dir-tile__locked-hover,
        .dir-tile--locked:focus-visible .dir-tile__locked-hover { display: inline; }

        /* ── Category sections ── */
        .dir-group { margin-bottom: 1.85rem; }

        .dir-group__head {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          margin: 0 0 0.85rem;
          padding-bottom: 0.6rem;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.92rem;
          font-weight: 700;
        }

        .dir-group__count {
          padding: 0.1rem 0.45rem;
          border: 1px solid rgba(233,30,140,0.25);
          border-radius: 99px;
          background: rgba(233,30,140,0.12);
          color: #F9A8D4;
          font-size: 0.68rem;
        }

        /* ── Empty ── */
        .dir-empty {
          padding: 4rem 1rem;
          color: #334155;
          text-align: center;
        }

        .dir-empty p {
          margin: 0.9rem 0 0;
          color: #64748B;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.9rem;
        }

        .dir-empty button {
          margin-top: 0.7rem;
          border: 0;
          background: none;
          color: #E91E8C;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
        }

        /* ── Profile dialog ── */
        .dir-modal {
          position: fixed;
          inset: 0;
          z-index: 120;
          display: grid;
          place-items: center;
          padding: 1.5rem;
          background: rgba(4,7,16,0.78);
          backdrop-filter: blur(6px);
        }

        .dir-modal__panel {
          position: relative;
          width: min(56rem, 100%);
          max-height: 90vh;
          overflow-y: auto;
          padding: 2.25rem 2.4rem 1.5rem;
          border: 1px solid rgba(233,30,140,0.28);
          border-radius: 1.5rem;
          background:
            radial-gradient(110% 70% at 12% 0%, rgba(88,28,135,0.42), transparent 62%),
            linear-gradient(155deg, #171233, #0B0A1B 65%);
          box-shadow: 0 32px 90px rgba(0,0,0,0.65);
        }

        .dir-modal__close {
          position: absolute;
          top: 1.4rem;
          right: 1.5rem;
          display: grid;
          place-items: center;
          width: 2.6rem;
          height: 2.6rem;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 50%;
          background: rgba(255,255,255,0.05);
          color: #CBD5E1;
          cursor: pointer;
          transition: color .18s ease, border-color .18s ease, background .18s ease;
        }

        .dir-modal__close:hover {
          color: #fff;
          border-color: rgba(233,30,140,0.45);
          background: rgba(233,30,140,0.14);
        }

        /* Identity on the left, the member's own words on the right, split by a
           hairline so neither column reads as a caption of the other. */
        .dir-modal__head {
          display: grid;
          /*
            Portrait and identity side by side on the first row, biography across
            the full width beneath. The name used to sit under the photo with the
            biography in a narrow column beside it, so a long biography was a tall
            thin ribbon next to a great deal of empty space.
          */
          grid-template-columns: auto minmax(0, 1fr);
          column-gap: 1.75rem;
          row-gap: 1.35rem;
          margin-bottom: 1.6rem;
        }

        /*
          The close button's clearance belongs to the row it shares, not to the
          whole block. As padding on the head it also shortened the biography
          beneath, which sits well below the button and needs the full width.
        */
        .dir-modal__identity { padding-right: 3rem; }

        .dir-modal__identity {
          grid-row: 1;
          grid-column: 2;
          align-self: center;
          min-width: 0;
        }

        .dir-modal__portrait {
          position: relative;
          grid-row: 1;
          grid-column: 1;
          justify-self: start;
          align-self: center;
          border-radius: 50%;
          box-shadow: 0 0 0 3px rgba(233,30,140,0.55), 0 12px 34px rgba(0,0,0,0.45);
        }

        .dir-modal__reachable {
          position: absolute;
          right: 0.35rem;
          bottom: 0.35rem;
          width: 0.95rem;
          height: 0.95rem;
          border: 2px solid #120E28;
          border-radius: 50%;
          background: #22C55E;
        }

        .dir-modal__name {
          margin: 0;
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 2.05rem;
          font-weight: 700;
          line-height: 1.15;
        }

        .dir-modal__industry {
          margin: 0.45rem 0 0;
          color: #CBD5E1;
          font-family: 'Inter', sans-serif;
          font-size: 0.95rem;
          line-height: 1.45;
        }

        .dir-modal__company {
          margin: 0.15rem 0 0;
          color: #94A3B8;
          font-family: 'Inter', sans-serif;
          font-size: 0.95rem;
          line-height: 1.45;
        }

        .dir-modal__facts {
          margin-top: 1.15rem;
          padding-top: 1.15rem;
          border-top: 1px solid rgba(255,255,255,0.09);
        }

        .dir-modal__fact {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          margin: 0 0 0.65rem;
          color: #CBD5E1;
          font-family: 'Inter', sans-serif;
          font-size: 0.88rem;
        }

        .dir-modal__fact:last-child { margin-bottom: 0; }
        .dir-modal__fact svg { flex-shrink: 0; color: #E91E8C; }

        /*
          The biography spans both columns, with the scroll hint layered over its
          bottom edge — hence the wrapper: the hint has to sit outside the scrolling
          box or it would scroll away with the text it describes.
        */
        .dir-modal__bioWrap {
          position: relative;
          grid-row: 2;
          grid-column: 1 / -1;
          min-width: 0;
          padding-top: 1.35rem;
          border-top: 1px solid rgba(255,255,255,0.09);
        }

        .dir-modal__bio {
          /*
            Seven lines, then it scrolls.

            The height comes from the paragraph's own type rather than a round
            figure: at 16rem the box was taller than the text in most profiles and
            shorter than seven lines in none of them, so it neither hugged a short
            biography nor clamped a long one where it was asked to. Declared as
            variables because the clamp and the text it clamps must agree — the
            paragraph rule below consumes the same two values.
          */
          --dir-bio-font-size: 0.95rem;
          --dir-bio-line-height: 1.78;
          --dir-bio-lines: 7;
          min-width: 0;
          /* No fixed height, so a one-line biography is one line tall. */
          max-height: calc(var(--dir-bio-lines) * var(--dir-bio-font-size) * var(--dir-bio-line-height));
          overflow-y: auto;
          overscroll-behavior: contain;
          scrollbar-color: rgba(233,30,140,0.68) rgba(255,255,255,0.04);
          scrollbar-gutter: stable;
          padding-right: 0.35rem;
        }

        /* Fades the last line so the text visibly continues rather than stopping. */
        .dir-modal__bioWrap[data-more]::after {
          content: "";
          position: absolute;
          right: 0;
          bottom: 0;
          left: 0;
          height: 4.5rem;
          background: linear-gradient(to bottom, rgba(18,14,40,0) 0%, rgba(18,14,40,0.92) 78%, #120E28 100%);
          border-radius: 0 0 0.5rem 0.5rem;
          pointer-events: none;
        }

        .dir-modal__bioMore {
          position: absolute;
          bottom: 0.15rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          padding: 0.2rem 0.6rem;
          border: 1px solid rgba(233,30,140,0.45);
          border-radius: 99px;
          background: rgba(233,30,140,0.16);
          color: #F9A8D4;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          pointer-events: none;
        }

        .dir-modal__bio:focus-visible {
          outline: 2px solid rgba(249,168,212,0.88);
          outline-offset: 0.45rem;
        }

        .dir-modal__bio::-webkit-scrollbar { width: 0.5rem; }
        .dir-modal__bio::-webkit-scrollbar-track { background: rgba(255,255,255,0.04); border-radius: 99px; }
        .dir-modal__bio::-webkit-scrollbar-thumb { background: rgba(233,30,140,0.62); border-radius: 99px; }
        .dir-modal__bio::-webkit-scrollbar-thumb:hover { background: rgba(249,168,212,0.88); }

        .dir-modal__bio p {
          margin: 0 0 1.1rem;
          color: #CBD5E1;
          font-family: 'Inter', sans-serif;
          /* The two values the seven-line clamp above is computed from. */
          font-size: var(--dir-bio-font-size);
          line-height: var(--dir-bio-line-height);
        }

        .dir-modal__bio p:last-child { margin-bottom: 0; }

        .dir-modal__card {
          margin-bottom: 0.9rem;
          padding: 1.15rem 1.25rem 1.25rem;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 1.1rem;
          background: rgba(255,255,255,0.025);
        }

        .dir-modal__message-action {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          min-height: 2.6rem;
          padding: 0.65rem 0.85rem;
          border: 1px solid rgba(249,168,212,0.3);
          border-radius: 0.7rem;
          color: #F9A8D4;
          background: rgba(233,30,140,0.1);
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.78rem;
          font-weight: 750;
          text-decoration: none;
          transition: border-color .18s ease, background .18s ease;
        }

        .dir-modal__message-action svg:last-child { margin-left: auto; color: #C4B5FD; }
        .dir-modal__message-action:hover,
        .dir-modal__message-action:focus-visible {
          border-color: rgba(249,168,212,0.65);
          background: rgba(233,30,140,0.18);
          outline: none;
        }

        .dir-modal__label {
          margin: 0 0 0.85rem;
          color: #8595AC;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }

        .dir-modal__chips { display: flex; flex-wrap: wrap; gap: 0.6rem; }

        .dir-modal__chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1.05rem;
          border: 1px solid rgba(233,30,140,0.32);
          border-radius: 99px;
          background: linear-gradient(120deg, rgba(233,30,140,0.2), rgba(147,51,234,0.16));
          color: #F9A8D4;
          font-family: 'Inter', sans-serif;
          font-size: 0.9rem;
        }

        .dir-modal__chip svg { flex-shrink: 0; }

        .dir-modal__contacts {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
          gap: 0.7rem;
        }

        .dir-modal__contact {
          display: flex;
          align-items: center;
          gap: 0.85rem;
          min-width: 0;
          padding: 0.8rem 0.95rem;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 0.9rem;
          background: rgba(255,255,255,0.03);
          text-decoration: none;
          transition: border-color .18s ease, background .18s ease;
        }

        .dir-modal__contact:hover {
          border-color: rgba(233,30,140,0.4);
          background: rgba(233,30,140,0.09);
        }

        .dir-modal__contact-icon {
          display: grid;
          place-items: center;
          flex-shrink: 0;
          width: 2.5rem;
          height: 2.5rem;
          border: 1px solid rgba(233,30,140,0.3);
          border-radius: 0.7rem;
          background: rgba(233,30,140,0.16);
          color: #F9A8D4;
        }

        .dir-modal__contact-icon--linkedin {
          border-color: rgba(99,102,241,0.45);
          background: rgba(79,70,229,0.28);
          color: #A5B4FC;
        }

        .dir-modal__contact-text { min-width: 0; }

        .dir-modal__contact-value {
          display: block;
          overflow: hidden;
          color: #fff;
          font-family: 'Inter', sans-serif;
          font-size: 0.88rem;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dir-modal__contact-label {
          display: block;
          margin-top: 0.1rem;
          color: #7E8CA3;
          font-family: 'Inter', sans-serif;
          font-size: 0.78rem;
        }

        .dir-modal__contact-go { flex-shrink: 0; margin-left: auto; color: #7E8CA3; }
        .dir-modal__contact:hover .dir-modal__contact-go { color: #F9A8D4; }

        .dir-modal__mark {
          display: flex;
          align-items: center;
          gap: 1.1rem;
          margin-top: 1.4rem;
        }

        .dir-modal__rule {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(233,30,140,0.4));
        }

        .dir-modal__rule:last-child {
          background: linear-gradient(90deg, rgba(233,30,140,0.4), transparent);
        }

        /* ── Focus, responsive, motion ── */
        .dir-page :is(button, a, input):focus-visible {
          outline: 2px solid #F472B6;
          outline-offset: 2px;
        }

        @media (max-width: 1023px) {
          .dir-body { grid-template-columns: minmax(0, 1fr); }
          .dir-rail { position: static; }
          .dir-rail__scroll { max-height: 17rem; }
        }

        /* Below two comfortable columns the split becomes a stack: identity
           first, then the bio, with the divider redrawn above it. */
        @media (max-width: 860px) {
          .dir-modal__panel { padding: 1.9rem 1.4rem 1.25rem; }
          .dir-modal__head { grid-template-columns: minmax(0, 1fr); }
          .dir-modal__portrait { grid-row: 1; grid-column: 1; }
          .dir-modal__identity { grid-row: 2; grid-column: 1; }
          .dir-modal__bioWrap { grid-row: 3; grid-column: 1; }
          .dir-modal__bio {
            margin-top: 1.4rem;
            padding: 1.4rem 0 0;
            border-left: 0;
            border-top: 1px solid rgba(255,255,255,0.09);
          }
        }

        @media (max-width: 720px) {
          .dir-shell { padding: 1.5rem 1rem 4rem; }
          .dir-modal { padding: 0.9rem; }
          .dir-modal__name { font-size: 1.5rem; }
          .dir-masthead { align-items: flex-start; gap: 1.35rem; }
          .dir-masthead__tools { width: 100%; }
          .dir-search { flex: 1; min-width: 0; }
          .dir-switch { width: 100%; }
          .dir-switch button { flex: 1; justify-content: center; }
          .dir-grid { grid-template-columns: repeat(auto-fill, minmax(9.5rem, 1fr)); }
        }

        @media (prefers-reduced-motion: reduce) {
          .dir-tile, .dir-cat, .dir-switch button, .dir-refine,
          .dir-modal__close, .dir-modal__contact { transition: none; }
          .dir-tile:hover { transform: none; }
        }
      `}</style>
      <Footer />
    </>
  );
}
